import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { getSessionId } from '@utils/session';

const ENDPOINT_URL = '/cart-shopping-carts/my-cart/add';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type AddProductToCartInput = paths[url]['post']['requestBody']['content']['application/json'];
export type AddProductToCartSuccessResponse = paths[url]['post']['responses']['200']['content']['application/json'];

/**
 * Requests endpoint to add product to shopping cart.
 */
export const useAddProductToCart = () => {
    const { getHeaders, handleCommonErrors } = useApiConfig();
    const { setCart } = useData();
    const [loading, setLoading] = useState<boolean>(false);
    const [lastSuccessfulResponseTime, setLastSuccessfulResponseTime] = useState<Date | null>(null);

    const sessionId = getSessionId();

    const request = async (props: AddProductToCartInput) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...props, session_id: sessionId }),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.error || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: AddProductToCartSuccessResponse = await response.json();

            setCart(data);
            setLastSuccessfulResponseTime(new Date());
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            handleCommonErrors(err);
        } finally {
            setLoading(false);
        }
    };

    return { request, loading, lastSuccessfulResponseTime };
};
